/**
 * Registre des modules métier affichés dans le Suite Shell (/modules).
 * Les `id` correspondent aux codes `tenant_modules.module` côté IAM.
 *
 * UI Kanakku split (juillet 2026) : billing, finance, procurement, inventory, product, crm.
 * Voir core/modules/module-handoff-config.ts pour ports et chemins d'atterrissage.
 */
export interface SuiteModuleEntry {
  id: string;
  displayName: string;
  description: string;
  /** Préfixes de permissions IAM accordant l'accès UI à ce module */
  permissionPrefixes: string[];
  /** Chemin handoff sur l'app produit (même origine ou URL absolue) */
  handoffPath: string;
}

export const SUITE_MODULE_REGISTRY: SuiteModuleEntry[] = [
  {
    id: "billing",
    displayName: "ITaaS Invoice",
    description: "Factures, clients, paiements, avoirs et portail client.",
    permissionPrefixes: ["billing."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "finance",
    displayName: "ITaaS Finance",
    description: "Trésorerie, comptes banque/caisse et rapports comptables Kanakku.",
    permissionPrefixes: ["finance."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "procurement",
    displayName: "ITaaS Procurement",
    description: "Achats, fournisseurs, commandes et notes de débit Kanakku.",
    permissionPrefixes: ["procurement."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "crm",
    displayName: "ITaaS CRM",
    description: "Contacts, pipeline, devis, livraisons et rapports commerciaux.",
    permissionPrefixes: ["crm."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "inventory",
    displayName: "ITaaS Inventory",
    description: "Stock, succursales, transferts et réquisitions Kanakku.",
    permissionPrefixes: ["catalog.", "warehouse.", "inventory.", "billing.branch.", "billing.stock."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "product",
    displayName: "ITaaS Product",
    description: "Catalogue articles PIM — produits, catégories et unités Kanakku.",
    permissionPrefixes: ["catalog."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "rh",
    displayName: "Ressources humaines",
    description: "Employés, congés, paie et performance.",
    permissionPrefixes: ["hr."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "workforce",
    displayName: "ITaaS Workforce",
    description: "Employés & organisation — création, postes, profils, N+1, organigramme.",
    permissionPrefixes: ["hr.", "employee.", "organization.", "department.", "position."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "recruitment",
    displayName: "ITaaS Recruitment",
    description: "Offres, candidats, pipeline et portail carrières /careers.",
    permissionPrefixes: ["hr.recruitment.", "recruitment.", "candidate."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "attendance",
    displayName: "ITaaS Attendance",
    description: "Présence, pointage, exceptions et lecteurs biométriques.",
    permissionPrefixes: ["attendance.", "hr.employee.read", "hr.attendance.read", "hr.attendance.write"],
    handoffPath: "/auth/handoff",
  },
  {
    id: "edu",
    displayName: "ITaaS Edu",
    description: "Gestion scolaire — élèves, classes, notes, bulletins, absences.",
    permissionPrefixes: ["edu."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "hospitol",
    displayName: "Hospitol",
    description: "Plateforme hospitalière modulaire — patients, pharmacie, labo, stock.",
    permissionPrefixes: ["hospitol."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "mineportal",
    displayName: "Mining Company Portal",
    description: "Portail entreprise minière — sites, sous-traitance ARSP, magasins, validations.",
    permissionPrefixes: ["mineportal."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "subportal",
    displayName: "ITaaS SubPortal",
    description: "Portail sous-traitant ARSP — conformité, lots et exécution terrain.",
    permissionPrefixes: ["subportal."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "legal",
    displayName: "ITaaS Legal",
    description: "Cabinet d'avocats — dossiers, échéances, temps passé, honoraires.",
    permissionPrefixes: ["legal."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "comex",
    displayName: "ITaaS Comex",
    description: "Transit, fret et douane RDC — dossiers GUICE et corridors Katanga.",
    permissionPrefixes: ["comex."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "erp",
    displayName: "ITaaS ERP",
    description: "Organisation et processus transverses (UI ERP legacy).",
    permissionPrefixes: ["erp."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "autoid",
    displayName: "ITaaS AutoID",
    description: "Scan, lecteurs USB/HID, codes-barres, QR et journal des scans.",
    permissionPrefixes: ["autoid."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "security",
    displayName: "ITaaS Security",
    description: "Gardiennage, rondes, incidents et conformité terrain.",
    permissionPrefixes: ["security."],
    handoffPath: "/auth/handoff",
  },
  {
    id: "notifications",
    displayName: "Notifications",
    description: "Boîte in-app et préférences de notification.",
    permissionPrefixes: ["notifications."],
    handoffPath: "/auth/handoff",
  },
];

/** Modules plateforme — pas proposés comme tuile métier par défaut */
export const SUITE_PLATFORM_MODULE_IDS = new Set(["iam"]);
